import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Mail, Phone, Clock, Trash2 } from 'lucide-react';
import { AdminNav } from './AdminDashboard';
import { useAuth } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import API from '../../lib/api';
import { toast } from 'sonner';

export default function AdminMessages() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (!authLoading && !isAdmin) navigate('/login'); }, [isAdmin, authLoading, navigate]);

  const load = () => {
    API.get('/admin/messages').then(r => { setMessages(r.data.messages || []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await API.delete(`/admin/messages/${id}`);
      setMessages(prev => prev.filter(m => m.id !== id));
      toast.success('Message deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-messages">
      <div className="bg-[#161616] border-b border-white/[0.06] py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <p className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">Admin</p>
          <h1 className="font-heading text-2xl text-white font-bold mt-1">Messages</h1>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <AdminNav active="/admin/messages" />
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-12 text-center">
            <MessageSquare className="w-10 h-10 text-white/15 mx-auto mb-4" />
            <h2 className="font-heading text-lg text-white font-semibold mb-2">No Messages Yet</h2>
            <p className="text-sm text-white/35">Contact form submissions will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((m, i) => (
              <motion.div key={m.id || i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-5" data-testid={`message-${m.id || i}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium text-sm">{m.name || 'Anonymous'}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-white/30">
                      {m.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</span>}
                      {m.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone}</span>}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(m.created_at)}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(m.id)} className="p-2 rounded-lg text-white/15 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0" title="Delete message" data-testid={`delete-message-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-white/50 leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
